import React, { useState } from 'react';
import {
  Box,
  Typography,
  Container,
  Grid,
  Card,
  CardContent,
  TextField,
  Button,
  useTheme,
  useMediaQuery,
  Alert,
  Snackbar,
  CircularProgress,
} from '@mui/material';
import { motion } from 'framer-motion';
import { Phone, Email, LocationOn, Send } from '@mui/icons-material';
import emailjs from '@emailjs/browser';

const ContactForm = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [formData, setFormData] = useState({
    name: '',
    contact: '',
    email: '',
    message: '',
  });
  const [open, setOpen] = useState(false);
  const [message, setMessage] = useState('');
  const [loading, setLoading] = useState(false);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    if (!formData.name || !formData.contact || !formData.email) {
      setMessage('Please fill in all required fields.');
      setOpen(true);
      setLoading(false);
      return;
    }

    try {
      // EmailJS configuration with your actual credentials
      const serviceId = 'service_qz1ezsp';
      const templateId = 'template_a8hhrmt';
      const publicKey = 'hu0WKWmhqVTtPOMHg';

      const templateParams = {
        name: formData.name,
        email: formData.email,
        contact_number: formData.contact,
        message: formData.message || 'No additional message provided',
        to_email: 'info@futureforwards.in',
      };

      await emailjs.send(serviceId, templateId, templateParams, publicKey);
      
      setMessage('Thank you for your inquiry! We will get back to you soon.');
      setOpen(true);
      setFormData({ name: '', contact: '', email: '', message: '' });
    } catch (error) {
      console.error('Email sending failed:', error);
      setMessage('Sorry, there was an error sending your message. Please try again or contact us directly.');
      setOpen(true);
    } finally {
      setLoading(false);
    }
  };

  const contactInfo = [
    {
      icon: <Phone sx={{ fontSize: '2rem' }} />,
      title: 'Phone',
      details: '+91 7356475308, +91 7736715551',
    },
    {
      icon: <Email sx={{ fontSize: '2rem' }} />,
      title: 'Email',
      details: 'info@futureforwards.in',
    },
    {
      icon: <LocationOn sx={{ fontSize: '2rem' }} />,
      title: 'Head Office',
      details: 'Trivandrum, Kerala, India',
    },
  ];

  return (
    <Box
      id="contact"
      sx={{
        py: { xs: 8, md: 12 },
        background: 'linear-gradient(135deg, #2c5aa0 0%, #1e3a5f 100%)',
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      {/* Background Elements */}
      <Box
        sx={{
          position: 'absolute',
          top: '10%',
          right: '10%',
          width: 200,
          height: 200,
          borderRadius: '50%',
          background: 'rgba(255, 255, 255, 0.1)',
          filter: 'blur(40px)',
        }}
      />
      <Box
        sx={{
          position: 'absolute',
          bottom: '20%',
          left: '10%',
          width: 150,
          height: 150,
          borderRadius: '50%',
          background: 'rgba(255, 107, 53, 0.1)',
          filter: 'blur(30px)',
        }}
      />

      <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 2 }}>
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
        >
          <Typography
            variant="h2"
            sx={{
              textAlign: 'center',
              mb: 2,
              color: 'white',
              fontWeight: 700,
            }}
          >
            Get in Touch!
          </Typography>
          <Typography
            variant="h6"
            sx={{
              textAlign: 'center',
              mb: 6,
              color: 'rgba(255, 255, 255, 0.9)',
              maxWidth: '600px',
              mx: 'auto',
            }}
          >
            Let's get started on your study abroad journey
          </Typography>
        </motion.div>

        <Grid container spacing={6} alignItems="center">
          <Grid item xs={12} md={6}>
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
            >
              <Box sx={{ mb: 4 }}>
                {contactInfo.map((info, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6, delay: index * 0.1 }}
                    viewport={{ once: true }}
                  >
                    <Card
                      sx={{
                        mb: 2,
                        background: 'rgba(255, 255, 255, 0.1)',
                        backdropFilter: 'blur(20px)',
                        border: '1px solid rgba(255, 255, 255, 0.2)',
                        borderRadius: 3,
                        transition: 'all 0.3s ease',
                        '&:hover': {
                          background: 'rgba(255, 255, 255, 0.15)',
                          transform: 'translateX(8px)',
                        },
                      }}
                    >
                      <CardContent sx={{ p: 3 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                          <Box sx={{ color: '#ff6b35' }}>
                            {info.icon}
                          </Box>
                          <Box>
                            <Typography
                              variant="h6"
                              sx={{
                                color: 'white',
                                fontWeight: 600,
                                mb: 0.5,
                              }}
                            >
                              {info.title}
                            </Typography>
                            <Typography
                              variant="body2"
                              sx={{
                                color: 'rgba(255, 255, 255, 0.8)',
                              }}
                            >
                              {info.details}
                            </Typography>
                          </Box>
                        </Box>
                      </CardContent>
                    </Card>
                  </motion.div>
                ))}
              </Box>
            </motion.div>
          </Grid>

          <Grid item xs={12} md={6}>
            <motion.div
              initial={{ opacity: 0, x: 50 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
            >
              <Card
                sx={{
                  background: 'rgba(255, 255, 255, 0.95)',
                  backdropFilter: 'blur(20px)',
                  borderRadius: 4,
                  boxShadow: theme.shadows[8],
                  border: '1px solid rgba(255, 255, 255, 0.2)',
                }}
              >
                <CardContent sx={{ p: 4 }}>
                  <form onSubmit={handleSubmit}>
                    <TextField
                      fullWidth
                      name="name"
                      label="Name"
                      value={formData.name}
                      onChange={handleChange}
                      required
                      sx={{ mb: 3 }}
                      InputProps={{
                        sx: {
                          borderRadius: 2,
                        },
                      }}
                    />
                    <TextField
                      fullWidth
                      name="contact"
                      label="Contact Number"
                      value={formData.contact}
                      onChange={handleChange}
                      required
                      sx={{ mb: 3 }}
                      InputProps={{
                        sx: {
                          borderRadius: 2,
                        },
                      }}
                    />
                    <TextField
                      fullWidth
                      name="email"
                      label="Email Address"
                      type="email"
                      value={formData.email}
                      onChange={handleChange}
                      required
                      sx={{ mb: 3 }}
                      InputProps={{
                        sx: {
                          borderRadius: 2,
                        },
                      }}
                    />
                    <TextField
                      fullWidth
                      name="message"
                      label="Message (Optional)"
                      multiline
                      rows={2}
                      value={formData.message}
                      onChange={handleChange}
                      sx={{ mb: 4 }}
                      InputProps={{
                        sx: {
                          borderRadius: 2,
                        },
                      }}
                    />
                    <motion.div
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                    >
                      <Button
                        type="submit"
                        variant="contained"
                        fullWidth
                        size="large"
                        disabled={loading}
                        startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <Send />}
                        sx={{
                          background: 'linear-gradient(135deg, #ff6b35 0%, #e55a2b 100%)',
                          borderRadius: 2,
                          py: 1.5,
                          fontSize: '1.1rem',
                          fontWeight: 600,
                          textTransform: 'none',
                          boxShadow: theme.shadows[3],
                          '&:hover': {
                            boxShadow: theme.shadows[5],
                          },
                          '&:disabled': {
                            background: 'rgba(0, 0, 0, 0.12)',
                            color: 'rgba(0, 0, 0, 0.26)',
                          },
                        }}
                      >
                        {loading ? 'Sending...' : 'Submit'}
                      </Button>
                    </motion.div>
                  </form>
                </CardContent>
              </Card>
            </motion.div>
          </Grid>
        </Grid>
      </Container>

      <Snackbar
        open={open}
        autoHideDuration={6000}
        onClose={() => setOpen(false)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setOpen(false)}
          severity={message.includes('Thank you') ? 'success' : 'warning'}
          sx={{ width: '100%' }}
        >
          {message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default ContactForm;
